import React, { createRef, useEffect } from 'react';
import { noop } from 'lodash';
import { translate } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { Form, FormControl, FormCounter, FormElement, Textarea, useForm } from '@fiverr-private/forms';
import { autoResizeTextareaHeight } from '../../../../../../common/util';
import { BriefSectionContentProps, BriefSectionViewContentProps } from '..';
import { SectionTypes } from '../../../../../../types';
import { useBriefContext } from '../../../../../../common/BriefContext';

const TEXTAREA_INPUT_LIMIT = 1000;

interface TextareaEditProps extends Pick<BriefSectionContentProps, 'section' | 'onChange'> {
  hideCounter?: boolean;
}

export const TextareaEdit = ({ section, onChange, hideCounter }: TextareaEditProps) => {
  const onContentChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onChange(e.target.value);
  const inputRef = createRef<HTMLInputElement>();
  const { scrollToSection } = useBriefContext();

  useEffect(() => {
    if (!inputRef?.current) {
      return;
    }

    // When EditArea opened then focus on the textarea element
    inputRef.current.setSelectionRange?.(inputRef.current.value.length, inputRef.current.value.length);
    inputRef.current.focus();
    scrollToSection(section.id);
    autoResizeTextareaHeight(inputRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useForm({
    defaultValues: {
      [section.id]: section.content,
    },
  });

  return (
    <Form methods={methods} onSubmit={noop}>
      <FormControl maxLength={TEXTAREA_INPUT_LIMIT} name={section.id}>
        <FormElement ref={inputRef}>
          <Textarea
            defaultValue={section.content}
            onChange={(e) => {
              onContentChange(e);
              autoResizeTextareaHeight(inputRef.current);
            }}
            maxLength={TEXTAREA_INPUT_LIMIT}
            overflowY="hidden"
            autoFocus
            placeholder={section.placeholder}
          />
        </FormElement>
        {!hideCounter && <FormCounter />}
      </FormControl>
    </Form>
  );
};

export const TextareaView = ({ section }: BriefSectionViewContentProps) => (
  <Text size="b_md" style={{ whiteSpace: 'pre-line' }}>
    {section.mandatory && section.type === SectionTypes.generated && !section.content
      ? `(${translate('ai_brief.sections.mandatory')})`
      : section.content}
  </Text>
);
